/**
 * What the admin create-match screen says when the server will REFUSE a member.
 *
 * WHY THIS EXISTS. /matches/new used to disclose ONE thing about an entered
 * member: the match-pool tier. The tier is a SCHEDULING control that an
 * admin-created match ignores on purpose, so that line always ends with "it
 * does not block the create". Everything that DOES block the create was
 * invisible until the operator had typed a reason and clicked: adminCreateMatch
 * runs the canonical eligibility predicate on both members (constraint 2 of 7,
 * backend/src/services/admin-match-create.ts) and refuses outright.
 *
 * So the screen could say the most reassuring thing it had - "Active" - about a
 * member the server was certain to refuse. Measured on PREDEV 2026-10-30:
 * 30,020 of 30,057 members in the view were in exactly that state (30,000
 * seeded fakes + 20 deleted accounts), every one of them reading "Active".
 *
 * These sentences are COPY, never logic. The verdict and the reason come from
 * the server, which runs the same predicate the create runs; this file only
 * turns one machine reason into one line an operator can act on. A verdict the
 * panel derived itself could disagree with the create, which is the defect this
 * fixes, pointing the other way.
 *
 * The keys are the literals of ELIGIBILITY_REASONS in
 * backend/src/utils/eligibility-reasons.ts, and
 * tests/unit/lint/every-eligibility-reason-has-an-operator-sentence.test.ts
 * fails the build when that vocabulary grows a reason this map cannot say.
 */

/** Prefix of the dynamic round-1 reason, which carries its own progress counts. */
export const ROUND1_INCOMPLETE_PREFIX = 'round1_incomplete';

/**
 * One clause per static reason, written to complete the sentence
 * "This member cannot be matched: <clause>."
 */
export const MATCH_BLOCK_CLAUSES: Readonly<Record<string, string>> = Object.freeze({
  user_not_found: 'no member has this ID',
  is_fake: 'the account is a seeded fake, not a real member',
  soft_deleted: 'the account is deleted',
  suspended: 'the account is suspended',
  signup_wizard_incomplete: 'the signup wizard is not finished',
  profile_incomplete: 'the profile is not finished',
  photos_incomplete: 'the required photos are not uploaded',
  hard_filters_incomplete: 'the hard filters are not finished',
  id_not_verified: 'the ID is not verified',
  phone_not_verified: 'the telephone number is not verified',
  display_name_missing: 'the account has no name to show',
  round1_not_configured:
    'onboarding round 1 has no active questions - a question-catalog fault, not this member',
});

/**
 * The clause for one machine reason.
 *
 * 🔴 It NEVER returns an empty string. A reason this file has no sentence for
 * still prints, raw, because a blank clause would read as "cannot be matched:"
 * with nothing after it - and the next criterion the backend adds would land
 * here first, before anyone updates this map.
 */
export function describeMatchBlock(reason: string | null | undefined): string {
  const r = (reason ?? '').trim();
  if (r === '') return 'the server did not say why';

  const known = MATCH_BLOCK_CLAUSES[r];
  if (known) return known;

  // The dynamic one: `round1_incomplete_<answered>_of_<total>`.
  const round1 = new RegExp(`^${ROUND1_INCOMPLETE_PREFIX}_(\\d+)_of_(\\d+)$`).exec(r);
  if (round1) {
    return `onboarding round 1 is not finished (${round1[1]} of ${round1[2]} answered)`;
  }

  return `the server refused this member (${r})`;
}
